import type { GptWrapper } from "./GptWrapper";
import type { Hooks } from "./AdEnginePlugin";
import type { FandomCmp } from "common-types";

import invariant from "tiny-invariant";

export type PrivacyHooks = Pick<Required<Hooks>, "onConsentUpdated">;

/**
 * Handles privacy for the ad engine by creating a singular place to query for if privacy apis
 * apply and check if the user has opted out. This is a wrapper around the {@link window.fandomCmp}
 * api that provides api agnostic privacy information.
 *
 * ## Plugin Usage
 *
 * For plugins in Odyssey, use the {@link Hooks["onConsentUpdated"]} plugin hook to get
 * notifications when privacy has updated. This is guranteed to be called at least once when
 * privacy apis are ready on ad engine load.
 *
 * ```ts
 * class MyPlugin extends AdEnginePlugin {
 *   hooks: Hooks = {
 *     onConsentUpdated({ optOut, blocking }: FandomCmp)  {
 *       if(blocking) {
 *          // kid wiki or blocked region, ads will not be served at all until updated to false
 *       }
 *
 *       if(optOut) {
 *         // user has opted out personalized ads
 *       }
 *
 *       // consent apis don't apply, or user has opted into personalized ads
 *     }
 *   }
 * }
 * ```
 *
 * ## Raw Consent Strings
 *
 * Some vendors require the actual usp or tcf strings and not just a setting for optOut. In these
 * cases, the {@link FandomCmp} object will contain two properties to retrieve the string and the
 * api that provided the string. This ensures that no code in odyssey will need to directly interact
 * with the consent apis.
 *
 * ```ts
 * class MyPlugin extends AdEnginePlugin {
 *   hooks: Hooks = {
 *     onConsentUpdated({ optOut, consentApi, consentString }: FandomCmp)  {
 *       // Check if any consent api was present. If no apis were on the page, then consentApi will
 *       // be undefined.
 *       if(consentApi) {
 *          if(consentApi === "usp") {
 *            // consentString is the value from usp, like `1YNN`
 *            setupVendorWithUspString(consentString)
 *          }
 *       }
 *     }
 *   }
 * }
 * ```
 */
export class PrivacyManager {
  /**
   * Reference to the gptWrapper so the gpt privacy config can be updated as soon as new privacy
   * values are detected.
   */
  private readonly gptWrapper: GptWrapper;
  /**
   * The current consentData from window.fandomCmp. If we are using the old cmp code still, then
   * this will hold a computed version of the cmp objected with data directly from tcf and usp apis.
   */
  private consentData?: FandomCmp;
  /**
   * Promise resolve function for when there is an active promise created in the `waitForConsents`
   * function. This allows the next `fandomConsentUpdate` to resolve that promise after it processes
   * eliminating the need for multiple listeners of the same event.
   */
  private pendingWaitForConsentResolver?: (consentData: FandomCmp) => void;
  /**
   * Promise resolve function for when there is an active promise created in the `waitForBlocking`
   * function. It serves the same purpose as pendingWaitForConsentResolver, only for blocking.
   */
  private pendingWaitForBlockingResolver?: (consentData: FandomCmp) => void;

  constructor(
    gptWrapper: GptWrapper,
    private readonly hooks: PrivacyHooks,
  ) {
    // set initial consentData if we happen to have it
    this.consentData = window.fandomCmp;
    this.gptWrapper = gptWrapper;

    // Start listening for any changes to consent.
    this.startConsentUpdateListener();
  }

  /**
   * Starts listening to the `fandomConsentUpdate` event on the window object which will notify us
   * when consent has changed from the stubs on the page or identity engine.
   *
   * If consent is already present on the page, this will get the consent data and notify gpt and
   * all plugins of consent. Otherwise it will wait for the consent ready signal from the window
   * consent event.
   */
  private startConsentUpdateListener() {
    const listener = () => {
      if (this.fandomCmpReady(window.fandomCmp)) {
        this.fandomConsentUpdateHandler();
      }
    };
    window.addEventListener("fandomConsentUpdate", listener);

    // Apply any consents we already have and ensure if we have any, we notify plugins via hook
    listener();
  }

  /**
   * Handler for the fandomConsentUpdate event on the window object. This will update the
   * {@link consentData} object on the class and notify all plugins of the updated consent.
   *
   * If there is a {@link pendingWaitForConsentResolver} from an active call to
   * {@link waitForConsents}, then that will be called as well to resolve the promise.
   *
   * If there is a {@link pendingWaitForBlockingResolver} from an active call to
   * {@link waitForAdsAllowed}, then that will be called as well to resolve the promise.
   */
  private async fandomConsentUpdateHandler() {
    this.consentData = window.fandomCmp!;

    // Notify downstream of changes
    this.updateGptPrivacySettings();
    this.pendingWaitForConsentResolver?.(this.consentData);
    this.pendingWaitForConsentResolver = undefined;

    if (this.consentData.allowed.ads) {
      this.pendingWaitForBlockingResolver?.(this.consentData);
      this.pendingWaitForBlockingResolver = undefined;
    }
    this.notifyPluginsOfConsentChange();
  }

  /**
   * If we have consentData, then update the gptWrapper with the latest data.
   */
  private updateGptPrivacySettings() {
    if (this.consentData) {
      this.gptWrapper.updatePrivacySettings(this.consentData);
    }
  }

  /**
   * Run the `onConsentUpdated` hook notifying all plugins of the consent change.
   */
  private notifyPluginsOfConsentChange() {
    invariant(
      this.consentData,
      "Expected window.fandomCmp to have consent data before calling onConsentUpdated",
    );
    this.hooks.onConsentUpdated(this.consentData);
  }

  private fandomCmpReady(fandomCmp?: FandomCmp): boolean {
    return fandomCmp?.ready === true;
  }

  /**
   * Waits for consent apis to load and resolves with consent data when `ready: true`.
   *
   * @returns Promise that resolves with the consent data when consent is available. It will never
   * resolve if the ready signal is never received from the consent stubs and libraries/identity
   * engine.
   */
  async waitForConsents(): Promise<FandomCmp> {
    return new Promise<FandomCmp>((resolve) => {
      if (
        this.consentData !== undefined &&
        this.fandomCmpReady(this.consentData)
      ) {
        resolve(this.consentData);
      }

      this.pendingWaitForConsentResolver = resolve;
    });
  }

  /**
   * Waits for window.fandomCmp.allowed.ads to be set to true. allowed.ads flag can be set to false in the following cases:
   * - the CMP modal has been displayed as user must provide their consent,
   * - the AgeGate modal has been displayed as user must provide their age.
   *
   * As soon as CMP modal is closed, and as soon as AgeGate modal is closed too (in GDPR both modals can show up),
   * the allowed.ads flag is set to true.
   *
   * @returns Promise that resolves with the consent data when allowed.ads flag is on. It will never
   * resolve if the allowed.ads stays false.
   */
  async waitForAdsAllowed(): Promise<FandomCmp> {
    return new Promise<FandomCmp>((resolve) => {
      if (
        this.consentData?.ready === true &&
        this.consentData?.allowed.ads === true
      ) {
        resolve(this.consentData);
      }

      this.pendingWaitForBlockingResolver = resolve;
    });
  }
}
